import type {
  AuthoredCard,
  CardInstanceState,
  HeroDefinition,
  MatchConfig,
  MatchState,
  SeatId,
  SeatState,
  SystemBattleBotsConfig,
} from "../../../schemas"
import { HERO_LEVEL_UP_COSTS } from "../../../schemas"
import { DEFAULT_SYSTEM_BATTLE_BOTS_CONFIG } from "../../../system/battleBots"
import {
  BASIC_BATTLE_BOTS_PER_PLAYER,
  MERC_ROW_VISIBLE_SLOTS,
} from "../content/cards"
import { seededShuffle } from "../rng"
import {
  buildCombatState,
  buildCombatStateForHeroLevel,
  drawCards,
} from "./state"

void HERO_LEVEL_UP_COSTS // referenced for documentation; level-up handled in command

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SeatInput = {
  seatId: SeatId
  playerId: string
  displayName: string
  hero: HeroDefinition
  mercCards: readonly AuthoredCard[] // exactly 24
  handicapBonusHealth?: number
}

export type CreateMatchInput = {
  matchId: string
  seatA: SeatInput
  seatB: SeatInput
  seed: string
  systemBattleBots?: SystemBattleBotsConfig
  starterDeck?: {
    basicBattleBots: number
    sparkBots: number
  }
  configOverrides?: Partial<Omit<MatchConfig, "mode" | "randomSeed">>
}

// ---------------------------------------------------------------------------
// Instance factory
// ---------------------------------------------------------------------------

let instanceCounter = 0

function nextInstanceId(prefix: string): string {
  return `${prefix}_${(++instanceCounter).toString(36)}`
}

function makeCardInstance(
  card: AuthoredCard,
  ownerSeatId: SeatId,
  zone: CardInstanceState["zone"],
): CardInstanceState {
  return {
    instanceId: nextInstanceId(card.id),
    definitionId: card.id,
    kind: card.kind,
    recruitCost: card.derivedCost,
    ownerSeatId,
    zone,
    currentAttack: card.stats.attack,
    currentHealth: card.stats.health,
    maxHealth: card.stats.health,
    combatState: buildCombatState(card),
    status: {
      exhausted: false,
      canAttackThisTurn: false, // set true during turn start recovery
      hasAttackedThisTurn: false,
      deployedThisTurn: false,
      remainingAttack: 0,
    },
    modifiers: [],
    memory: { koCount: 0, timesActivated: 0 },
  }
}

function makeHeroInstance(
  hero: HeroDefinition,
  level: 1 | 2 | 3,
  ownerSeatId: SeatId,
): CardInstanceState {
  const levelDef = hero.progression[level - 1]
  return {
    instanceId: nextInstanceId(`hero_${hero.heroId}`),
    definitionId: hero.heroId,
    kind: "hero",
    ownerSeatId,
    zone: "support",
    currentAttack: levelDef.card.stats.attack,
    currentHealth: levelDef.card.stats.health,
    maxHealth: levelDef.card.stats.health,
    combatState: buildCombatStateForHeroLevel(hero, level),
    status: {
      exhausted: false,
      canAttackThisTurn: false,
      hasAttackedThisTurn: false,
      deployedThisTurn: false,
      remainingAttack: 0,
    },
    modifiers: [],
    memory: { koCount: 0, timesActivated: 0 },
  }
}

// ---------------------------------------------------------------------------
// Seat setup
// ---------------------------------------------------------------------------

function setupSeat(
  input: SeatInput,
  basicBattleBot: AuthoredCard,
  sparkBot: AuthoredCard,
  starterDeck: NonNullable<CreateMatchInput["starterDeck"]>,
  seed: string,
  rngCallIndex: number,
  _startingHandSize: number,
  startingCCHealth: number,
): { seat: SeatState; instances: CardInstanceState[]; rngCallIndex: number } {
  const instances: CardInstanceState[] = []

  // Hero instance → support
  const heroInst = makeHeroInstance(input.hero, 1, input.seatId)
  instances.push(heroInst)

  if (starterDeck.basicBattleBots < 1) {
    throw new Error("Starter deck must include at least one Basic Battle Bot.")
  }

  // Starter units: 1 Basic Battle Bot to active, remaining starters to draw deck.
  const bbbInstances: CardInstanceState[] = Array.from(
    { length: starterDeck.basicBattleBots },
    () => makeCardInstance(basicBattleBot, input.seatId, "deck"),
  )
  const bbbIds = bbbInstances.map((b) => b.instanceId)
  const activeBbbId = bbbIds[0]
  const drawDeckStarterInstances: CardInstanceState[] = [
    ...bbbInstances.slice(1),
    ...Array.from({ length: starterDeck.sparkBots }, () =>
      makeCardInstance(sparkBot, input.seatId, "deck"),
    ),
  ]
  const drawDeckStarterIds = drawDeckStarterInstances.map(
    (card) => card.instanceId,
  )
  rngCallIndex = seededShuffle(drawDeckStarterIds, seed, rngCallIndex)

  // Promote one BBB to active with ready-to-attack state.
  const activatedBbbs: CardInstanceState[] = bbbInstances.map((b) =>
    b.instanceId === activeBbbId
      ? {
          ...b,
          zone: "active" as const,
          status: {
            ...b.status,
            canAttackThisTurn: true,
            hasAttackedThisTurn: false,
            remainingAttack: b.currentAttack,
          },
        }
      : b,
  )
  instances.push(...activatedBbbs, ...drawDeckStarterInstances)

  // Supply deck: 24 authored mercs, shuffled
  const supplyInstances: CardInstanceState[] = input.mercCards.map((card) =>
    makeCardInstance(card, input.seatId, "supply"),
  )
  const supplyIds = supplyInstances.map((s) => s.instanceId)
  rngCallIndex = seededShuffle(supplyIds, seed, rngCallIndex)

  // Initial merc row: first N ids from shuffled supply → merc_row
  const mercRowIds = supplyIds.slice(0, MERC_ROW_VISIBLE_SLOTS)
  const remainingSupplyIds = supplyIds.slice(MERC_ROW_VISIBLE_SLOTS)
  const mercRowSet = new Set(mercRowIds)

  const updatedSupplyInstances: CardInstanceState[] = supplyInstances.map(
    (inst) =>
      mercRowSet.has(inst.instanceId)
        ? { ...inst, zone: "merc_row" as const }
        : inst,
  )
  instances.push(...updatedSupplyInstances)

  const baseHealth = startingCCHealth
  const handicapBonus = input.handicapBonusHealth ?? 0

  const seat: SeatState = {
    seatId: input.seatId,
    playerId: input.playerId,
    displayName: input.displayName,
    centralCommand: {
      name: "Central Command",
      baseHealth,
      handicapHealthBonus: handicapBonus,
      currentHealth: baseHealth + handicapBonus,
    },
    resources: {
      maxEnergy: 1,
      currentEnergy: 1,
    },
    hero: {
      heroId: input.hero.heroId,
      currentLevel: 1,
      currentInstanceId: heroInst.instanceId,
    },
    zones: {
      active: activeBbbId,
      support: [heroInst.instanceId],
      hand: [],
      drawDeck: drawDeckStarterIds,
      discard: [],
      scrap: [],
      supply: remainingSupplyIds,
      mercRow: mercRowIds,
    },
    deckRef: `deck_${input.seatId}`,
    oncePerTurn: {
      hasUsedNormalSwap: false,
      hasRecruitedThisTurn: false,
    },
  }

  return { seat, instances, rngCallIndex }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function createMatch(input: CreateMatchInput): {
  state: MatchState
  allInstances: CardInstanceState[]
} {
  instanceCounter = 0 // reset for determinism per match
  const systemBattleBots =
    input.systemBattleBots ?? DEFAULT_SYSTEM_BATTLE_BOTS_CONFIG
  const starterDeck = input.starterDeck ?? {
    basicBattleBots: BASIC_BATTLE_BOTS_PER_PLAYER,
    sparkBots: 2,
  }

  const config: MatchConfig = {
    mode: "friend_pvp",
    randomSeed: input.seed,
    startingCentralCommandHealth:
      input.configOverrides?.startingCentralCommandHealth ?? 30,
    supportCapacity: input.configOverrides?.supportCapacity ?? 3,
    handSizeLimit: input.configOverrides?.handSizeLimit ?? 3,
    startingHandSize: input.configOverrides?.startingHandSize ?? 5,
    energyCurveMax: input.configOverrides?.energyCurveMax ?? 4,
    systemBattleBots,
    handicap: input.configOverrides?.handicap,
  }

  let rngCallIndex = 0

  const resultA = setupSeat(
    input.seatA,
    systemBattleBots.basicBattleBot,
    systemBattleBots.sparkBot,
    starterDeck,
    input.seed,
    rngCallIndex,
    config.startingHandSize,
    config.startingCentralCommandHealth,
  )
  rngCallIndex = resultA.rngCallIndex

  const resultB = setupSeat(
    input.seatB,
    systemBattleBots.basicBattleBot,
    systemBattleBots.sparkBot,
    starterDeck,
    input.seed,
    rngCallIndex,
    config.startingHandSize,
    config.startingCentralCommandHealth,
  )
  rngCallIndex = resultB.rngCallIndex

  const allInstances = [...resultA.instances, ...resultB.instances]
  const instancesMap: Record<string, CardInstanceState> = {}
  for (const inst of allInstances) {
    instancesMap[inst.instanceId] = inst
  }

  const preState: MatchState = {
    matchId: input.matchId,
    rulesEdition: 0,
    rulesRevision: 1,
    contentVersion: "dev",
    status: "active",
    config,
    seats: [resultA.seat, resultB.seat],
    activeSeat: "seat_a",
    prioritySeat: "seat_a",
    turn: {
      number: 1,
      step: "main",
      actionWindow: "open",
      actionCount: 0,
    },
    rngCallIndex,
    instances: instancesMap,
    log: [],
  }

  // Draw seat_a's initial hand (seat_b draws at the start of their first turn via end-turn logic)
  const state = drawCards(preState, "seat_a", config.handSizeLimit)

  return { state, allInstances }
}
